import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Button, Flashbar, Modal, Box, SpaceBetween, Spinner, AppLayout, Alert } from '@amzn/awsui-components-core';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContextProvider';
import { useEventsApiContext } from '../../context/EventsApiContextProvider';
import './EAHuddleHomePage.scss';

// Hooks
import { useAttendeeManagement } from './hooks/useAttendeeManagement';
import { useDocumentProcessing } from './hooks/useDocumentProcessing';
import { useFlashMessages } from './hooks/useFlashMessages';
import { useRecentMeetings } from './hooks/useRecentMeetings';
import { useScheduling } from './hooks/useScheduling';
import { useSharedMeeting } from './hooks/useSharedMeeting';
import { useMeetingPersistence } from './hooks/useMeetingPersistence';
import { useNotifications } from './hooks/useNotifications';

// Containers
import { MeetingFormContainer } from './containers/MeetingFormContainer';
import { AddAttendeesContainer } from './containers/AddAttendeesContainer';
import { AttendeePathPicker, AttendeePath } from './containers/AttendeePathPicker';
import { UploadTemplateContainer } from './containers/modals/UploadTemplateModal';
import { ImportFromMeetingModal } from './containers/modals/ImportFromMeetingModal';
import { SchedulingContainer } from './containers/scheduling/SchedulingContainer';
import { TimezoneConverterContainer } from './containers/TimezoneConverterContainer';
// Components
import { InfoPanel } from './components/InfoPanel';
import { HyperBadge } from '../../generic-components/hyper-badge/HyperBadge';
import { EmployeeService } from './services/EmployeeService';

// Types & utils
import { Employee, AttendeePriority, RepeatOption, TimeSlot } from './interfaces/interface';
import { DateTime } from 'luxon';

import { exportAttendeeHierarchyToCsv } from './utils/csvExportUtils';
import { generateMultiBadgerUrl, loadAttendeesInBatches, computeAuthorizedLogins, buildMeetingAttendees } from './utils/attendeeUtils';
import { EmployeeSettingsService } from './services/EmployeeSettingsService';
import { MeetingService } from './services/MeetingService';
import { EAHuddleService } from 'src/services/EAHuddleService';
import { logger } from 'src/logger';

export const EAHuddleHomePage: React.FC = () => {
  const { Alias, DisplayName } = useAuth();
  const nameParts = DisplayName?.split(',') || [];
  const firstName = nameParts[1]?.trim();
  const fullName = nameParts.length >= 2 ? `${nameParts[1]?.trim()} ${nameParts[0]?.trim()}` : DisplayName;
  const eventsApiContext = useEventsApiContext();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── UI state ─────────────────────────────────────────────────────────
  const [showUploadTemplateModal, setShowUploadTemplateModal] = useState(false);
  const [showRefreshWarning, setShowRefreshWarning] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(true);

  // ── Attachment state ─────────────────────────────────────────────────
  const [uploadedAttachments, setUploadedAttachments] = useState<Array<{ fileName: string; s3Uri: string }>>([]);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [proposedSlot, setProposedSlot] = useState<{ date: string; startTime: string; endTime: string; timezone: string } | null>(null);
  const [hoveredSlotId, setHoveredSlotId] = useState<string | null>(null);
  const [populateSlot, setPopulateSlot] = useState<{ date: string; startTime: string; endTime: string; timezone: string } | null>(null);
  const [directAddSlot, setDirectAddSlot] = useState<{ date: string; startTime: string; endTime: string; timezone: string } | null>(null);

  // ── Attendee path picker state ───────────────────────────────────────
  const [attendeePathChosen, setAttendeePathChosen] = useState(false);
  const [initialShowCopyFromMeeting, setInitialShowCopyFromMeeting] = useState(false);
  const [initialShowUpload, setInitialShowUpload] = useState(false);
  const [isImportingAttendees, setIsImportingAttendees] = useState(false);
  const [pendingOnBehalfOfName, setPendingOnBehalfOfName] = useState<string | null>(null);

  // ── Refresh state ────────────────────────────────────────────────────
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());
  const [refreshedAgo, setRefreshedAgo] = useState('just now');

  useEffect(() => {
    const update = () => {
      const diff = DateTime.now().diff(DateTime.fromJSDate(lastRefreshedAt), 'minutes').minutes;
      setRefreshedAgo(diff < 1 ? 'just now' : DateTime.fromJSDate(lastRefreshedAt).toRelative() ?? '');
    };
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [lastRefreshedAt]);

  const [pendingTemplateFile, setPendingTemplateFile] = useState<File | null>(null);
  const [creatorEmployee, setCreatorEmployee] = useState<Employee | null>(null);

  useEffect(() => {
    logger.info('User authenticated successfully!', { app: 'EA Huddle' });
  }, []);

  // ── Core hooks ───────────────────────────────────────────────────────
  const { flashMessages, addFlashMessage, removeFlashMessage } = useFlashMessages();

  const {
    resolvedAttendees,
    handleAddAttendee,
    handleAttendeeUpdate,
    handleAttendeeRemove,
    handleAddProxy,
    getExistingEmails,
    findDuplicateEmails,
    replaceDocumentAttendees,
    resortAttendees,
    favoriteLoginNames,
    toggleFavorite: togglePersistedFavorite,
    refreshFavorites
  } = useAttendeeManagement(Alias);

  const { isProcessingDocument, documentError, handleFormattedContentExtracted, processDocument } = useDocumentProcessing();
  const {
    recentMeetings,
    allRecentMeetings,
    totalMeetingCount,
    currentPage,
    totalPages,
    goToPage,
    addRecentMeeting,
    updateMeetingSubject,
    deleteRecentMeeting,
    refreshMeetings
  } = useRecentMeetings(Alias);

  // ── Shared meeting + access control ──────────────────────────────────
  const sharedMeeting = useSharedMeeting({
    alias: Alias,
    meetingId: searchParams.get('meetingId'),
    creator: searchParams.get('creator'),
    addFlashMessage,
    setMeetingFormData: (updater) => persistence.setMeetingFormData(updater),
    handleAddAttendee,
    setInitialSnapshot: (snapshot) => persistence.setInitialSnapshot(snapshot)
  });

  // Fetch creator employee details when access is denied
  useEffect(() => {
    if (!sharedMeeting.isAccessDenied || !sharedMeeting.sharedCreator) return;
    EmployeeService.getEmployeeByLogin(sharedMeeting.sharedCreator)
      .then(setCreatorEmployee)
      .catch(() => {});
  }, [sharedMeeting.isAccessDenied, sharedMeeting.sharedCreator]);

  // Load attachments from DDB when meeting loads
  useEffect(() => {
    if (!sharedMeeting.sharedMeetingId || !sharedMeeting.sharedCreator) return;
    EmployeeSettingsService.getMeeting(sharedMeeting.sharedCreator, sharedMeeting.sharedMeetingId)
      .then((meeting) => {
        if (meeting?.attachments && meeting.attachments.length > 0) {
          setUploadedAttachments(
            meeting.attachments.map((s3Uri) => ({
              fileName: s3Uri.split('/').pop() ?? 'attachment',
              s3Uri
            }))
          );
        }
      })
      .catch(() => {});
  }, [sharedMeeting.sharedMeetingId, sharedMeeting.sharedCreator]);

  // ── Meeting persistence ──────────────────────────────────────────────
  const persistence = useMeetingPersistence({
    alias: Alias,
    creatorAlias: sharedMeeting.sharedCreator,
    sharedMeetingId: sharedMeeting.sharedMeetingId,
    isSharedCreator: sharedMeeting.isSharedCreator,
    resolvedAttendees,
    isLoadingSharedMeeting: sharedMeeting.isLoadingSharedMeeting,
    addFlashMessage,
    addRecentMeeting,
    updateRecentMeetingSubject: updateMeetingSubject,
    setSearchParams: (params, options) => setSearchParams(params, options)
  });

  // ── Scheduling ───────────────────────────────────────────────────────
  const scheduling = useScheduling({
    meetingId: sharedMeeting.sharedMeetingId,
    creatorAlias: sharedMeeting.sharedCreator,
    currentUserAlias: Alias,
    currentUserName: firstName || Alias,
    meetingSubject: persistence.meetingFormData.subject
  });

  // ── Notifications ────────────────────────────────────────────────────
  // On homepage or as creator: show all notifications. As non-creator on shared link: scope to that meeting.
  const notificationMeetingFilter = sharedMeeting.sharedMeetingId && !scheduling.isCreator ? sharedMeeting.sharedMeetingId : null;
  const notificationActions = useNotifications({
    currentUserAlias: Alias,
    meetingId: notificationMeetingFilter
  });

  // ── Refresh handler ──────────────────────────────────────────────────
  const unresolvedAttendees = useMemo(
    () => resolvedAttendees.filter((a) => a.status === 'notfound' || a.status === 'multiple' || !a.selectedEmployee),
    [resolvedAttendees]
  );

  // Warn on browser refresh/close if there are unresolved attendees
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unresolvedAttendees.length > 0) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unresolvedAttendees.length]);

  const handleRefresh = useCallback(async () => {
    if (!sharedMeeting.sharedMeetingId || !sharedMeeting.sharedCreator) return;
    setIsRefreshing(true);
    try {
      // Refresh meeting metadata
      const meetingData = await EmployeeSettingsService.getMeeting(sharedMeeting.sharedCreator, sharedMeeting.sharedMeetingId);
      if (meetingData) {
        persistence.setMeetingFormData((prev) => ({
          ...prev,
          ...(meetingData.subject ? { subject: meetingData.subject } : {}),
          ...(meetingData.agenda !== undefined ? { agenda: meetingData.agenda } : {}),
          ...(meetingData.duration ? { duration: meetingData.duration } : {}),
          ...(meetingData.repeat ? { repeat: meetingData.repeat } : {}),
          ...(meetingData.onBehalfOfLogin !== undefined ? { onBehalfOfLogin: meetingData.onBehalfOfLogin } : {})
        }));

        // Refresh attendees — diff against current list
        if (meetingData.attendees) {
          const ddbLogins = new Set(meetingData.attendees.map((a) => a.loginName.toLowerCase()));
          const currentLogins = new Set(resolvedAttendees.map((a) => (a.selectedEmployee?.login_name || a.inputName).toLowerCase()));

          // Remove attendees no longer in DDB
          resolvedAttendees.forEach((a) => {
            const login = (a.selectedEmployee?.login_name || a.inputName).toLowerCase();
            if (!ddbLogins.has(login)) handleAttendeeRemove(a.id);
          });

          // Add attendees that are new in DDB
          const newAttendees = meetingData.attendees.filter((a) => !currentLogins.has(a.loginName.toLowerCase()));
          if (newAttendees.length > 0) {
            await loadAttendeesInBatches(newAttendees, handleAddAttendee);
          }
        }
      }
      // Refresh scheduling data (time slots, votes, comments)
      await scheduling.loadSchedulingData();
      await notificationActions.refresh();
      await Promise.all([refreshFavorites(), refreshMeetings()]);
      setLastRefreshedAt(new Date());
    } catch (error) {
      logger.error('Failed to refresh meeting data', { error });
    } finally {
      setIsRefreshing(false);
    }
  }, [
    sharedMeeting.sharedMeetingId,
    sharedMeeting.sharedCreator,
    scheduling,
    persistence,
    resolvedAttendees,
    handleAddAttendee,
    handleAttendeeRemove,
    notificationActions,
    refreshFavorites,
    refreshMeetings
  ]);

  const handleRefreshClick = useCallback(() => {
    if (unresolvedAttendees.length > 0) {
      setShowRefreshWarning(true);
    } else {
      handleRefresh();
    }
  }, [unresolvedAttendees.length, handleRefresh]);

  // Restore onBehalfOfLogin and meta fields from loaded shared meeting data
  useEffect(() => {
    if (!sharedMeeting.loadedMeetingData) return;
    persistence.setMeetingFormData((prev) => ({
      ...prev,
      ...(sharedMeeting.loadedMeetingData!.onBehalfOfLogin ? { onBehalfOfLogin: sharedMeeting.loadedMeetingData!.onBehalfOfLogin } : {})
    }));
  }, [sharedMeeting.loadedMeetingData]);

  // Sync duration/repeat: scheduling data (DDB) takes precedence over shared meeting snapshot
  useEffect(() => {
    const source = scheduling.schedulingData ?? sharedMeeting.loadedMeetingData;
    if (source?.duration || source?.repeat) {
      persistence.setMeetingFormData((prev) => ({
        ...prev,
        ...(source?.duration ? { duration: source.duration } : {}),
        ...(source?.repeat ? { repeat: source.repeat } : {})
      }));
    }
  }, [scheduling.schedulingData, sharedMeeting.loadedMeetingData]);

  // ── Favorites ────────────────────────────────────────────────────────
  const existingLoginNames = React.useMemo(
    () => new Set(resolvedAttendees.map((a) => a.selectedEmployee?.login_name).filter(Boolean) as string[]),
    [resolvedAttendees]
  );

  const handleFavoriteClick = useCallback(
    (loginName: string, priority: AttendeePriority = AttendeePriority.Optional) => {
      const normalizedLogin = loginName.toLowerCase();
      if (existingLoginNames.has(normalizedLogin)) return;

      const employee: Employee = {
        login_name: normalizedLogin,
        employee_name: '',
        employee_internal_email_address: '',
        job_title_name: '',
        location_city_name: '',
        location_state_name: '',
        job_level_name: '',
        department_name_current: ''
      };
      handleAddAttendee(employee, priority);
    },
    [existingLoginNames, handleAddAttendee]
  );

  const handleToggleFavorite = useCallback(
    (id: string) => {
      const attendee = resolvedAttendees.find((a) => a.id === id);
      if (attendee) {
        const loginName = attendee.selectedEmployee?.login_name;
        handleAttendeeUpdate(id, { isFavorite: !attendee.isFavorite });
        if (loginName) {
          togglePersistedFavorite(loginName);
        }
      }
    },
    [resolvedAttendees, handleAttendeeUpdate, togglePersistedFavorite]
  );

  // ── Derived values ───────────────────────────────────────────────────
  const existingEmails = getExistingEmails();
  const duplicateEmails = findDuplicateEmails();
  const executiveAssistants = MeetingService.collectExecutiveAssistants(resolvedAttendees);

  // ── Document handling ────────────────────────────────────────────────
  const handleFileSelected = useCallback(
    async (file: File) => {
      const result = await processDocument(file, eventsApiContext, Alias, persistence.manuallyEditedMeetingFormDataFields);
      if (!result.success) return;

      if (result.formUpdates) {
        persistence.setMeetingFormData((prev) => ({ ...prev, ...result.formUpdates }));
      }
      if (result.parsedAttendees) {
        replaceDocumentAttendees(result.parsedAttendees);
      }

      setShowUploadTemplateModal(false);
      setInitialShowUpload(false);
      setAttendeePathChosen(true);

      if (result.unresolvedOnBehalfOf) {
        setPendingOnBehalfOfName(result.unresolvedOnBehalfOf);
      }

      // Auto-register meeting if not yet registered (path picker flow)
      const meetingId =
        sharedMeeting.sharedMeetingId ??
        (await persistence.ensureMeetingCreated({
          subject: result.formUpdates?.subject ?? persistence.meetingFormData.subject,
          agenda: result.formUpdates?.agenda ?? persistence.meetingFormData.agenda,
          duration: result.formUpdates?.duration ?? (persistence.meetingFormData.duration || 30),
          repeat: result.formUpdates?.repeat ?? (persistence.meetingFormData.repeat || 'none'),
          onBehalfOfLogin: result.formUpdates?.onBehalfOfLogin ?? persistence.meetingFormData.onBehalfOfLogin
        }));

      // Upload template as meeting attachment
      if (meetingId) {
        const organizer = sharedMeeting.sharedCreator ?? Alias;
        const uploadResult = await EAHuddleService.uploadMeetingAttachment(file, organizer, meetingId);
        if (uploadResult.success && uploadResult.s3Uri) {
          await EmployeeSettingsService.appendAttachment(organizer, meetingId, uploadResult.s3Uri);
          logger.info('Template uploaded as attachment', { meetingId, s3Uri: uploadResult.s3Uri });
        }
      }
    },
    [
      processDocument,
      eventsApiContext,
      Alias,
      persistence.manuallyEditedMeetingFormDataFields,
      replaceDocumentAttendees,
      persistence.setMeetingFormData,
      sharedMeeting
    ]
  );

  // Flush pending template file upload when meeting is created
  useEffect(() => {
    if (!sharedMeeting.sharedMeetingId || !pendingTemplateFile) return;
    const meetingId = sharedMeeting.sharedMeetingId;
    const organizer = sharedMeeting.sharedCreator ?? Alias;
    const file = pendingTemplateFile;
    setPendingTemplateFile(null);

    (async () => {
      const uploadResult = await EAHuddleService.uploadMeetingAttachment(file, organizer, meetingId);
      if (uploadResult.success && uploadResult.s3Uri) {
        await EmployeeSettingsService.appendAttachment(organizer, meetingId, uploadResult.s3Uri);
        logger.info('Pending template uploaded as attachment', { meetingId, s3Uri: uploadResult.s3Uri });
      }
    })();
  }, [sharedMeeting.sharedMeetingId, pendingTemplateFile, sharedMeeting.sharedCreator, Alias]);

  // ── Duration / Repeat handlers (for MeetingFormContainer) ────────────
  const handleDurationChange = useCallback(
    (value: number) => {
      persistence.setMeetingFormData((prev) => ({ ...prev, duration: value }));
      if (sharedMeeting.sharedMeetingId) {
        scheduling.updateDuration(value);
      }
    },
    [sharedMeeting.sharedMeetingId, scheduling, persistence]
  );

  const handleRepeatChange = useCallback(
    (value: RepeatOption) => {
      persistence.setMeetingFormData((prev) => ({ ...prev, repeat: value }));
      if (sharedMeeting.sharedMeetingId) {
        scheduling.updateRepeat(value);
      }
    },
    [sharedMeeting.sharedMeetingId, scheduling, persistence]
  );

  // ── Simple callbacks ─────────────────────────────────────────────────
  const handleCreateMeeting = useCallback(
    (selectedSlot?: TimeSlot) => {
      const outlookUrl = MeetingService.generateOutlookWebUrl(persistence.meetingFormData, resolvedAttendees, selectedSlot, fullName, Alias);
      window.open(outlookUrl, '_blank');
    },
    [persistence.meetingFormData, resolvedAttendees, fullName, Alias]
  );

  const handleSendNoteToExecutiveAssistants = useCallback(() => {
    try {
      const mailtoUrl = MeetingService.generateEAMailtoUrl(
        persistence.meetingFormData,
        resolvedAttendees,
        scheduling.activeTimeSlots,
        sharedMeeting.sharedMeetingId,
        sharedMeeting.sharedCreator ?? Alias,
        undefined,
        fullName,
        Alias
      );
      window.open(mailtoUrl, '_blank');
    } catch (error) {
      addFlashMessage('error', (error as Error).message || 'Failed to send note to Executive Assistants.');
    }
  }, [
    persistence.meetingFormData,
    resolvedAttendees,
    addFlashMessage,
    scheduling.activeTimeSlots,
    sharedMeeting.sharedMeetingId,
    sharedMeeting.sharedCreator,
    Alias,
    fullName
  ]);

  const handleAddAttendeeWithTypes = useCallback(
    (employee: Employee, priority: AttendeePriority) => {
      handleAddAttendee(employee, priority);
    },
    [handleAddAttendee]
  );

  const handleMultiBadgerClick = () => {
    const logins = resolvedAttendees.map((a) => a.selectedEmployee?.login_name).filter(Boolean) as string[];
    window.open(generateMultiBadgerUrl(logins), '_blank');
  };

  const handleDownloadCsv = () => {
    exportAttendeeHierarchyToCsv(resolvedAttendees, (errorMessage) => {
      addFlashMessage('error', errorMessage);
    });
  };

  const handleAttendeePathSelect = useCallback(
    async (path: AttendeePath) => {
      if (path === 'upload') {
        setInitialShowUpload(true);
      } else if (path === 'clone') {
        setInitialShowCopyFromMeeting(true);
      } else {
        try {
          await persistence.ensureMeetingCreated({
            subject: persistence.meetingFormData.subject,
            agenda: persistence.meetingFormData.agenda,
            duration: persistence.meetingFormData.duration || 30,
            repeat: persistence.meetingFormData.repeat || 'none',
            onBehalfOfLogin: persistence.meetingFormData.onBehalfOfLogin
          });
          setAttendeePathChosen(true);
        } catch {
          addFlashMessage('error', 'Failed to create meeting. Please try again.');
        }
      }
    },
    [persistence, addFlashMessage]
  );

  // Determine if this is a shared link (has meetingId in URL)
  const isSharedLink = !!searchParams.get('meetingId');

  const noop = useCallback(() => {}, []);
  const sharedFormProps = {
    organizerAlias: sharedMeeting.sharedCreator ?? Alias,
    subject: persistence.meetingFormData.subject,
    agenda: persistence.meetingFormData.agenda,
    duration: persistence.meetingFormData.duration,
    repeat: persistence.meetingFormData.repeat,
    onBehalfOfLogin: persistence.meetingFormData.onBehalfOfLogin,
    isRegistered: !!sharedMeeting.sharedMeetingId,
    meetingId: sharedMeeting.sharedMeetingId,
    creatorAlias: sharedMeeting.sharedCreator ?? Alias,
    uploadedAttachments
  };

  // ── Meeting form element (shared between home page and Create Meeting modal) ──
  const meetingFormElement = (
    <MeetingFormContainer
      {...sharedFormProps}
      onSubjectChange={(value) => persistence.handleInputChange('subject', value)}
      onAgendaChange={(value) => persistence.handleInputChange('agenda', value)}
      onDurationChange={handleDurationChange}
      onRepeatChange={handleRepeatChange}
      onBehalfOfChange={(login) => {
        const previousLogin = persistence.meetingFormData.onBehalfOfLogin;
        persistence.setMeetingFormData((prev) => ({ ...prev, onBehalfOfLogin: login }));
        if (sharedMeeting.sharedMeetingId && sharedMeeting.sharedCreator) {
          const attendees = buildMeetingAttendees(resolvedAttendees);
          EmployeeSettingsService.updateMeetingMetadata(sharedMeeting.sharedCreator, sharedMeeting.sharedMeetingId, {
            onBehalfOfLogin: login,
            authorizedLogins: computeAuthorizedLogins(sharedMeeting.sharedCreator, attendees, {
              organizerLogin: sharedMeeting.sharedCreator,
              onBehalfOfLogin: login
            })
          }).catch((error) => {
            logger.error('Failed to persist onBehalfOf change', { error, meetingId: sharedMeeting.sharedMeetingId });
            persistence.setMeetingFormData((prev) => ({ ...prev, onBehalfOfLogin: previousLogin }));
          });
        }
      }}
      onRegisterMeeting={(metadata) => persistence.handleCreateSharedLink(metadata)}
      isSaving={persistence.isSavingMeeting}
      isReadOnly={sharedMeeting.isSharedViewer}
      defaultEditing={attendeePathChosen}
      onUploadedAttachmentsChange={setUploadedAttachments}
      pendingOnBehalfOfName={pendingOnBehalfOfName}
      onPendingOnBehalfOfDismiss={() => setPendingOnBehalfOfName(null)}
    />
  );

  const meetingFormReadOnly = (
    <MeetingFormContainer
      {...sharedFormProps}
      onSubjectChange={noop}
      onAgendaChange={noop}
      onDurationChange={noop}
      onRepeatChange={noop}
      onBehalfOfChange={noop}
      onRegisterMeeting={noop}
      isReadOnly={true}
      onUploadedAttachmentsChange={noop}
      pendingOnBehalfOfName={pendingOnBehalfOfName}
      onPendingOnBehalfOfDismiss={() => setPendingOnBehalfOfName(null)}
    />
  );

  // ── Main layout ──────────────────────────────────────────────────────
  return (
    <>
      <AppLayout
        headerSelector="#h"
        toolsHide={true}
        navigation={
          <InfoPanel
            favoriteLoginNames={favoriteLoginNames}
            existingLoginNames={existingLoginNames}
            onFavoriteClick={handleFavoriteClick}
            recentMeetings={recentMeetings}
            totalMeetingCount={totalMeetingCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            onDeleteMeeting={deleteRecentMeeting}
            isReadOnly={sharedMeeting.isSharedViewer}
            isMeetingRegistered={!!sharedMeeting.sharedMeetingId}
            notificationActions={notificationActions}
            isRefreshing={isRefreshing}
            onRefresh={sharedMeeting.sharedMeetingId && !sharedMeeting.isAccessDenied ? handleRefreshClick : undefined}
            refreshedAgo={refreshedAgo}
          />
        }
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        notifications={
          flashMessages.length > 0 ? (
            <Flashbar
              items={flashMessages.map((message) => ({
                type: message.type,
                content: message.content,
                dismissible: true,
                onDismiss: () => removeFlashMessage(message.id),
                id: message.id
              }))}
            />
          ) : undefined
        }
        contentType="table"
        maxContentWidth={Number.MAX_VALUE}
        content={
          <>
            {sharedMeeting.isAccessDenied ? (
              <Box textAlign="center" padding={{ top: 'xxxl', bottom: 'xxxl' }}>
                <SpaceBetween size="m" alignItems="center">
                  <Box variant="h2">Access Denied</Box>
                  <Box variant="p" color="text-body-secondary">
                    You don't have access to this meeting. This can happen if your EA assignment changed recently or if you are not an attendee.
                  </Box>
                  <Box variant="p" color="text-body-secondary">
                    Please ask the meeting creator to refresh their meeting page to update access, or request to be added as an attendee.
                  </Box>
                  {sharedMeeting.sharedCreator && (
                    <Box>
                      <Box variant="p" color="text-body-secondary" margin={{ bottom: 'xs' }}>
                        Meeting creator:
                      </Box>
                      {creatorEmployee ? <HyperBadge employee={creatorEmployee} /> : <Spinner size="normal" />}
                    </Box>
                  )}
                </SpaceBetween>
              </Box>
            ) : sharedMeeting.isLoadingSharedMeeting ? (
              <Box textAlign="center" padding={{ top: 'xxxl', bottom: 'xxxl' }}>
                <SpaceBetween size="s" alignItems="center">
                  <Spinner size="large" />
                  <Box fontSize="body-m" color="text-body-secondary">
                    {sharedMeeting.sharedMeetingProgress || 'Loading meeting data...'}
                  </Box>
                </SpaceBetween>
              </Box>
            ) : (
              <>
                {!attendeePathChosen && !sharedMeeting.isSharedViewer && !isSharedLink && (
                  <div className="meeting-content-grid">
                    <AttendeePathPicker
                      onSelect={handleAttendeePathSelect}
                      displayName={firstName}
                      disabledPaths={isProcessingDocument ? new Set<AttendeePath>(['clone', 'manual']) : undefined}
                    />
                  </div>
                )}
                {(attendeePathChosen || sharedMeeting.isSharedViewer || isSharedLink) && meetingFormElement}
                <div className="meeting-content-grid">
                  {!!sharedMeeting.sharedMeetingId && (attendeePathChosen || sharedMeeting.isSharedViewer || isSharedLink) && (
                    <>
                      {isImportingAttendees ? (
                        <Box padding={{ vertical: 'l' }}>
                          <SpaceBetween size="s" direction="vertical" alignItems="center">
                            <Spinner size="large" />
                            <Box fontSize="body-m" color="text-body-secondary">
                              Loading attendees from meeting...
                            </Box>
                          </SpaceBetween>
                        </Box>
                      ) : (
                        <AddAttendeesContainer
                          initialExpanded={true}
                          formData={persistence.meetingFormData}
                          onInputChange={persistence.handleInputChange}
                          initialShowCopyFromMeeting={initialShowCopyFromMeeting}
                          initialShowUpload={initialShowUpload}
                          attendees={{
                            resolvedAttendees,
                            existingEmails,
                            duplicateEmails,
                            onAddAttendee: handleAddAttendeeWithTypes,
                            onAttendeeUpdate: handleAttendeeUpdate,
                            onAttendeeRemove: handleAttendeeRemove,
                            onToggleFavorite: handleToggleFavorite,
                            onAddProxy: handleAddProxy,
                            onResort: resortAttendees
                          }}
                          document={{
                            isProcessingDocument,
                            documentError,
                            onFileSelected: handleFileSelected,
                            onFormattedContentExtracted: handleFormattedContentExtracted,
                            showUploadModal: showUploadTemplateModal,
                            onHideUploadModal: () => setShowUploadTemplateModal(false)
                          }}
                          sharedMeeting={{
                            isLoadingSharedMeeting: sharedMeeting.isLoadingSharedMeeting,
                            sharedMeetingProgress: sharedMeeting.sharedMeetingProgress,
                            isReadOnly: sharedMeeting.isSharedViewer
                          }}
                          executiveAssistants={executiveAssistants}
                          onMultiBadgerClick={handleMultiBadgerClick}
                          onDownloadCsv={handleDownloadCsv}
                          recentMeetings={recentMeetings}
                        />
                      )}
                      <SchedulingContainer
                        initialExpanded={true}
                        organizerLogin={sharedMeeting.sharedCreator ?? Alias}
                        currentUserAlias={Alias}
                        currentUserName={Alias}
                        scheduling={{
                          ...scheduling,
                          isCreator: sharedMeeting.sharedMeetingId ? scheduling.isCreator : true
                        }}
                        onEnsureMeetingCreated={persistence.ensureMeetingCreated}
                        isReadOnly={sharedMeeting.isSharedViewer}
                        executiveAssistants={executiveAssistants}
                        onSendNoteToEAs={handleSendNoteToExecutiveAssistants}
                        meetingId={sharedMeeting.sharedMeetingId}
                        onCreateMeeting={handleCreateMeeting}
                        resolvedAttendeesCount={resolvedAttendees.length}
                        meetingSubject={persistence.meetingFormData.subject}
                        meetingDuration={persistence.meetingFormData.duration}
                        onBehalfOfLogin={persistence.meetingFormData.onBehalfOfLogin}
                        resolvedAttendees={resolvedAttendees}
                        meetingFormContent={meetingFormElement}
                        meetingFormReadOnly={meetingFormReadOnly}
                        onSelectedSlotChange={setSelectedSlotId}
                        onProposedSlotChange={setProposedSlot}
                        onHoveredSlotChange={setHoveredSlotId}
                        populateSlot={populateSlot}
                        directAddSlot={directAddSlot}
                      />
                      <TimezoneConverterContainer
                        resolvedAttendees={resolvedAttendees}
                        duration={persistence.meetingFormData.duration}
                        activeTimeSlots={scheduling.activeTimeSlots}
                        selectedSlotId={selectedSlotId}
                        proposedSlot={proposedSlot}
                        hoveredSlotId={hoveredSlotId}
                        onTimeWindowClick={setPopulateSlot}
                        onTimeWindowDoubleClick={setDirectAddSlot}
                        isReadOnly={sharedMeeting.isSharedViewer}
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </>
        }
      />

      {initialShowUpload && (
        <UploadTemplateContainer
          visible={initialShowUpload}
          onClose={() => setInitialShowUpload(false)}
          isProcessingDocument={isProcessingDocument}
          documentError={documentError}
          onFileSelected={handleFileSelected}
          onFormattedContentExtracted={handleFormattedContentExtracted}
        />
      )}

      {showRefreshWarning && (
        <Modal
          visible={true}
          onDismiss={() => setShowRefreshWarning(false)}
          header="Unresolved Attendees"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setShowRefreshWarning(false)}>
                  Cancel
                </Button>
                <span className="blue-button">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowRefreshWarning(false);
                      handleRefresh();
                    }}
                  >
                    Refresh Anyway
                  </Button>
                </span>
              </SpaceBetween>
            </Box>
          }
        >
          <Alert type="warning" statusIconAriaLabel="Warning">
            You have {unresolvedAttendees.length} unresolved attendee{unresolvedAttendees.length !== 1 ? 's' : ''} that will be lost on refresh
            because they haven't been saved to the meeting. Resolve or remove them first, or continue to refresh anyway.
          </Alert>
        </Modal>
      )}
      <ImportFromMeetingModal
        visible={initialShowCopyFromMeeting}
        onDismiss={() => setInitialShowCopyFromMeeting(false)}
        recentMeetings={allRecentMeetings}
        resolvedAttendees={resolvedAttendees}
        onImportComplete={async (attendees) => {
          setInitialShowCopyFromMeeting(false);
          setIsImportingAttendees(true);
          try {
            if (!sharedMeeting.sharedMeetingId) {
              await persistence.ensureMeetingCreated({
                subject: persistence.meetingFormData.subject,
                agenda: persistence.meetingFormData.agenda,
                duration: persistence.meetingFormData.duration || 30,
                repeat: persistence.meetingFormData.repeat || 'none',
                onBehalfOfLogin: persistence.meetingFormData.onBehalfOfLogin
              });
            }
            if (attendees.length > 0) {
              await loadAttendeesInBatches(attendees, handleAddAttendee);
            }
            setAttendeePathChosen(true);
          } catch {
            addFlashMessage('error', 'Failed to import attendees. Please try again.');
          } finally {
            setIsImportingAttendees(false);
          }
        }}
      />
    </>
  );
};
